import React, { FC, useEffect, useState } from 'react'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import store, { useAppDispatch, useAppSelector } from 'store/store'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { getAndCheckTokenCaptCha } from 'utils/authentication'
import SignIn from './SignIn'
import {
  COMPANY_APPROVED_MODAL,
  CONFIRM_PASSWORD_MODAL,
  EDIT_PHONE_NUMBER_MODAL,
  GO_TO_STEP1_MODAL,
  GO_TO_STEP1_SWITCH_MODAL,
  MERGE_MODAL,
  RESET_PASSWORD_MODAL,
  REVOLUTION_MODAL,
  SIGN_IN_MODAL,
  SIGN_UP_MODAL,
  SWITCH_ACCOUNT_MODAL,
  VERIFICATION_CODE_MODAL,
} from 'components/new_booking/guest_flow/constants'

import {
  updateSubAccountCheckBox,
  addSubAccount,
  signInProgressFinished,
} from 'store/actions/common/bookingActionCreators'
import accountAction, { IParamSignUp } from 'store/actions/common/accountAction'
import { currentCustomerActionsCreator } from 'store/toolkit/currentCustomer/currentCustomer.reducer'
import { accountManageActions } from 'store/toolkit/accountManage/accountManage.reducer'
import { currentStepActionsCreator } from 'store/toolkit/currentStep/currentStep.reducer'
import { nextStep } from 'store/actions/new_booking/stepActionCreators'
import { currentVehicleType } from 'utils/new_booking/common'

import { hideLoading, showLoading } from 'assets/javascripts/webapp-v2/common'
import CommonUtils from 'utils/common'
import CustomerAPI from 'api/customers'
import TallyUtils from 'utils/booking/TallyUtils'
import { generateAppsFlyerEventObj } from 'utils/trackingAppsFlyer'
import { crossStorageUtils } from 'utils/crossStorage'
import paths from 'routers/paths'
import { IUserInfo } from 'interface/IAccountManage'
import SignUp from './SignUp'
import WelcomeRevolution from './WelcomeRevolution'
import VerificationCode from './VerificationCode'
import EditPhoneNumber from './EditPhoneNumber'
import toastr from 'utils/toast'
import ResetPassword from './ResetPassword'
import { AccountManagementWrapper } from './SIndex'
import ConfirmNewPassword from './ConfirmNewPassword'
import { Utils } from 'utils/Utils'
import MergeAccount from './MergeAccount'
import GoToStep1 from './GoToStep1'
import ChooseAccount from './ChooseAccount'
import CompanyApproved from './CompanyApproved'
import StorageKeys from 'constants/storage-keys'
import { extraServicesActionsCreator } from 'store/toolkit/extraServices/extraServices.reducer'
import { filter, find } from 'lodash-es'
import { STEP_1_TO_2 } from 'constants/bookingConstants'

export interface SocialInfo {
  _provider: string
  _profile: Profile
  _token: Token
}
interface Token {
  accessToken: string
  idToken: string
  scope: string
  expiresIn: number
  firstIssued_at: number
  expiresAt: number
}
interface Profile {
  id: string
  name: string
  firstName: string
  lastName: string
  email: string
  profilePicURL: string
}
export interface MergeInfo {
  countryCode?: string
  email: string
  phone: string
  firstName: string
  lastName: string
  businessName: string
  referredBy: string
  access_token: string
  provider: string
  socialToken: string
}

const AccountManagementView: FC<{ view: string }> = ({ view }) => {
  const {
    currentCustomer,
    currentStep,
    extraInfos,
    accountModal,
    locations,
    timeType,
    serviceTypes,
    selectedVehicleTypeID,
    selectedServiceTypeID,
    booking,
  } = useAppSelector((s) => s)
  const pendingNextStep = useAppSelector((s) => s.accountModal?.pendingNextStep)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { pathname, search } = useLocation()
  const [isSignUpSocial, setIsSignUpSocial] = useState(false)
  const [selectedEmployId, setSelectedEmployId] = useState<number>(-1)
  const [dataSignUp, setDataSignUp] = useState<IParamSignUp>({
    country_code: '',
    email: '',
    phone: '',
    first_name: '',
    last_name: '',
    business_name: '',
    referred_by: '',
    password: '',
    access_token: '',
    provider: '',
    identity: '',
  })
  const [dataMergeSocial, setDataMergeSocial] = useState<MergeInfo>({
    countryCode: '',
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    businessName: '',
    referredBy: '',
    access_token: '',
    provider: '',
    socialToken: '',
  })

  const [prePassword, setPrePassword] = useState('')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isSignInPage = matchPath(pathname || '', paths.SIGN_IN)
  const isSignUpPage = matchPath(pathname || '', paths.SIGN_UP)
  const isNoStep3 = !!(isSignInPage || isSignUpPage)
  const checkBackToStep1 = (customerData?: IUserInfo) => {
    if (isNoStep3) return
    if (pendingNextStep === STEP_1_TO_2 && currentStep === 1) {
      dispatch(accountManageActions.clearPendingNextStep())
      const state = store.getState()
      const vehicleType = currentVehicleType(state)
      const isSaverVehicle = vehicleType?.is_saver || false
      const customer = customerData || currentCustomer
      if (customer.currentCompanyId && isSaverVehicle) {
        hideLoading()
      } else {
        const defaultService = filter(serviceTypes, (e) => e.id === selectedServiceTypeID)?.[0] || {}
        const vehicleType = find(defaultService?.vehicle_types, { id: selectedVehicleTypeID })
        const tallyData = TallyUtils.calculateTallyData(
          {
            locations,
            vehicleTypeID: selectedVehicleTypeID,
            timeType,
          },
          { booking, specificStep: currentStep, vehicleType } as any
        )

        const trackingData = {
          price: tallyData?.price > 0 ? tallyData?.price : 0,
          distance: tallyData?.distance > 0 ? tallyData?.distance : 0,
          transitTime: tallyData?.transitTime > 0 ? tallyData?.transitTime : 0,
        }

        const currentServiceType = find(serviceTypes, (e) => e.id === selectedServiceTypeID)
        const appsFlyerEventObj = generateAppsFlyerEventObj({
          extraInfos,
          currentServiceType,
          timeType,
          currentCustomer: customer,
          booking,
          locations,
          currentVehicleType: vehicleType,
        })

        CommonUtils.trackStep1ToStep2Success(trackingData, customer, appsFlyerEventObj, true)

        nextStep(navigate)(dispatch, store.getState)
      }
      return
    }
    hideLoading()
  }

  const checkRedirectPage = () => {
    if (isNoStep3) {
      showLoading()
      navigate(isSignInPage ? `/${search}` : '/')
      hideLoading()
    }
  }

  const trackSwitchAccountEventMoengage = async (
    currentCustomer: IUserInfo,
    id?: number | null,
    newCurrentCustomer?: IUserInfo
  ) => {
    const { isMultiple, isBatchEZ, isSmartLoad } = CommonUtils
    if (!isMultiple() && !isBatchEZ() && !isSmartLoad()) {
      const { oldId, newId } = CommonUtils.getMoengageId(currentCustomer, id)
      try {
        await CommonUtils.moengageTrackEvent('Switch Account', {
          'From Account': oldId,
          'To Account': newId,
          'At Step': CommonUtils.getAtStepAttributeMoengage(currentStep),
        })
        await CommonUtils.moengageDestroySession()
        await CommonUtils.handleTrackMoengage(newCurrentCustomer, pendingNextStep)
      } catch (err) {
        console.error(err)
      }
    }
  }

  const switchAccountHandler = async (companyId: number | null, currentCustomerId: number, isSwitchStep3?: boolean) => {
    showLoading()
    dispatch(updateSubAccountCheckBox(false))
    dispatch(addSubAccount())
    const accountId = !companyId ? currentCustomerId : companyId
    const newCurrentCustomer = await accountAction.switchAccount(accountId)(dispatch)
    if (isSwitchStep3) await trackSwitchAccountEventMoengage(currentCustomer, companyId, newCurrentCustomer)
    else await CommonUtils.handleTrackMoengage(currentCustomer, pendingNextStep)
    if (!isSwitchStep3) checkBackToStep1()
    dispatch(signInProgressFinished(true))
    accountAction.getSubAccountTags(companyId)(dispatch)
  }

  const confirmSwitchAccount = () => {
    localStorage.setItem(StorageKeys.SWITCH_ACCOUNT_ATTACHMENTS, 'true')
    dispatch(extraServicesActionsCreator.setPrevCompanyId(currentCustomer.currentCompanyId || 0))
    switchAccountHandler(selectedEmployId, currentCustomer.id, true)
  }

  const handleSocialLogin = async (socialInfo: SocialInfo, err: { error: string }) => {
    if (err) {
      if (err.error === 'popup_closed_by_user') {
        const error = { message: t('webapp.action.login_social_incognito') }
        toastr.options = {
          positionClass: `toast-top-center ${isNoStep3 ? 'toast-social-login' : 'step3-social-login'}`,
        }
        Utils.showToastrMessage('info', error.message)
      }
      return null
    }
    if (socialInfo) {
      const profile = socialInfo?._profile || {}
      const provider = socialInfo?._provider || ''
      const accessToken =
        provider === 'facebook' ? socialInfo._token.accessToken || '' : socialInfo._token.idToken || ''
      const params = {
        email: profile.email,
        provider,
        access_token: accessToken,
      }
      showLoading()
      let xRecaptchaToken
      if (extraInfos?.enable_captcha_verification) {
        xRecaptchaToken = await getAndCheckTokenCaptCha(executeRecaptcha, 'login_social')
      }
      const result = await CustomerAPI.socialLogin(params, xRecaptchaToken)
      const data = result.data
      if (!data.success) {
        if (result.status === 400 && data.error) {
          hideLoading()
          return toastr.error(data.error)
        }
        if (data.error_code === 403) {
          const error = { message: t('errors.messages.suspended') }
          toastr.options = {
            positionClass: `toast-top-center ${isNoStep3 ? 'toast-social-login' : 'step3-social-login'}`,
          }
          hideLoading()
          return Utils.showToastrMessage('error', data.message || error.message)
        }
        if (!data.object) {
          setDataSignUp((s) => ({
            ...s,
            email: profile.email,
            first_name: profile.firstName,
            last_name: profile.lastName,
            access_token: accessToken,
            provider,
          }))
          setIsSignUpSocial(true)
          dispatch(accountManageActions.updateModalAccountManage(SIGN_UP_MODAL))
        } else {
          setDataMergeSocial({
            countryCode: data?.object?.country_code || '',
            email: data?.object?.email || '',
            phone: data?.object?.phone || '',
            firstName: data?.object?.first_name || '',
            lastName: data?.object?.last_name || '',
            businessName: data?.object?.business_name || '',
            referredBy: data?.object?.referred_by || '',
            access_token: accessToken || '',
            provider: provider || '',
            socialToken: accessToken,
          })
          dispatch(accountManageActions.updateModalAccountManage(MERGE_MODAL))
        }
      } else {
        const { area_id: areaID } = extraInfos
        const response: IUserInfo = await CustomerAPI.getCustomer(data?.access_token, { area_id: areaID })
        if (response?.statusApi === 200) {
          crossStorageUtils.setAccessToken(data.access_token)
          dispatch(accountManageActions.updateModalAccountManage(''))

          if (!response.last_login_employ_id) {
            const newCustomer = {
              ...response,
              authentication_token: data?.access_token,
            }
            dispatch(currentCustomerActionsCreator.updateCustomer(newCustomer))
            checkBackToStep1(newCustomer)
            dispatch(signInProgressFinished(true))
            CommonUtils.handleTrackMoengage(newCustomer, pendingNextStep)
          } else {
            await switchAccountHandler(response.last_login_employ_id, response.id)
          }
          checkRedirectPage()
        }
      }
      hideLoading()
    }
  }

  const showModal = () => {
    switch (accountModal.modalType) {
      case SIGN_IN_MODAL:
        return (
          <SignIn
            isNoStep3={isNoStep3}
            onCheckRedirectPage={checkRedirectPage}
            onSetPrePassword={setPrePassword}
            onSocialLogin={handleSocialLogin}
            onCheckBackToStep1={checkBackToStep1}
            executeRecaptcha={executeRecaptcha}
          />
        )
      case MERGE_MODAL:
        return (
          <MergeAccount
            isNoStep3={isNoStep3}
            onCheckRedirectPage={checkRedirectPage}
            dataMergeSocial={dataMergeSocial}
            executeRecaptcha={executeRecaptcha}
          />
        )
      case SIGN_UP_MODAL:
        return (
          <SignUp
            onCheckBackToStep1={checkBackToStep1}
            isNoStep3={isNoStep3}
            onCheckRedirectPage={checkRedirectPage}
            dataSignUp={dataSignUp}
            onSetDataSignUp={setDataSignUp}
            onSocialLogin={handleSocialLogin}
            isSignUpSocial={isSignUpSocial}
            executeRecaptcha={executeRecaptcha}
          />
        )
      case RESET_PASSWORD_MODAL:
        return (
          <ResetPassword
            isNoStep3={isNoStep3}
            enableCaptchaVerification={extraInfos?.enable_captcha_verification}
            executeRecaptcha={executeRecaptcha}
          />
        )
      case REVOLUTION_MODAL:
        return <WelcomeRevolution isNoStep3={isNoStep3} />
      case VERIFICATION_CODE_MODAL:
        return (
          <VerificationCode
            isNoStep3={isNoStep3}
            onCheckBackToStep1={checkBackToStep1}
            onCheckRedirectPage={checkRedirectPage}
            enableCaptchaVerification={extraInfos?.enable_captcha_verification}
            executeRecaptcha={executeRecaptcha}
          />
        )
      case EDIT_PHONE_NUMBER_MODAL:
        return <EditPhoneNumber isNoStep3={isNoStep3} setDataSignUp={setDataSignUp} dataSignUp={dataSignUp} />
      case SWITCH_ACCOUNT_MODAL:
        return <ChooseAccount setSelectedEmployId={setSelectedEmployId} />
      case CONFIRM_PASSWORD_MODAL:
        return <ConfirmNewPassword isNoStep3={isNoStep3} prePassword={prePassword} />
      case GO_TO_STEP1_SWITCH_MODAL:
        return <GoToStep1 confirmSwitchAccount={confirmSwitchAccount} />
      case GO_TO_STEP1_MODAL:
        return <GoToStep1 />
      case COMPANY_APPROVED_MODAL:
        return <CompanyApproved onCheckBackToStep1={checkBackToStep1} />
      default:
        return <></>
    }
  }
  useEffect(() => {
    hideLoading()
    if (view) {
      dispatch(currentStepActionsCreator.changeStep(1))
      dispatch(accountManageActions.updateModalAccountManage(view))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  useEffect(() => {
    if (
      currentCustomer.id &&
      !currentCustomer.verify_sms_code &&
      !currentCustomer.sms_confirmed_at &&
      !accountModal.modalType
    ) {
      dispatch(accountManageActions.updateModalAccountManage(VERIFICATION_CODE_MODAL))
    }
    if (currentCustomer.id) setDataSignUp((s) => ({ ...s, phone: currentCustomer.phone }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCustomer])

  return <AccountManagementWrapper isNoStep3={isNoStep3}>{showModal()}</AccountManagementWrapper>
}

export default AccountManagementView
